
import React, { useRef, useState, useEffect } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const InvoicePage = () => {
  const invoiceRef = useRef();
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [hoverState, setHoverState] = useState({
    submit: false,
    print: false,
    download: false
  });

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle hover effects
  const handleHover = (button, isHovered) => {
    setHoverState(prev => ({ ...prev, [button]: isHovered }));
  };

  // ✅ Sample Data
  const invoiceData = {
    invoiceNo: "INV-2025-001",
    date: new Date().toLocaleDateString(),
    time: new Date().toLocaleTimeString(),
    customer: {
      receiver: "Vikash Kumar Singh",
      phone: "+91 9572540788",
      location: "Balnagar FlyOver, Hyderabad",
      nearby: "Near BigC Mobile",
    },
    restaurant: "Maamaa's House - IstaraHouse",
    items: [
      { id: 1, category: "Main Course", type: "Veg", name: "Paneer Butter Masala", qty: 2, price: 200 },
      { id: 2, category: "Bread", type: "Veg", name: "Butter Naan", qty: 4, price: 50 },
      { id: 3, category: "Main Course", type: "Non-Veg", name: "Chicken Curry", qty: 3, price: 250 },
      { id: 4, category: "Mixed", type: "Mixed", name: "Combo Platter", qty: 2, price: 300 },
    ],
    deliveryCharge: 50,
    platformCharge: 30,
    discountPercent: 10,
    sgstPercent: 9,
    cgstPercent: 9,
    advancePaid: 500,
  };

  // ✅ Totals
  const subTotal = invoiceData.items.reduce((sum, item) => sum + item.qty * item.price, 0);
  const discount = (subTotal * invoiceData.discountPercent) / 100;
  const sgst = (subTotal * invoiceData.sgstPercent) / 100;
  const cgst = (subTotal * invoiceData.cgstPercent) / 100;
  const grandTotal = subTotal - discount + sgst + cgst + invoiceData.deliveryCharge + invoiceData.platformCharge;
  const dues = grandTotal - invoiceData.advancePaid;

  // ✅ Plate Summary
  const vegPlates = invoiceData.items.filter(i => i.type === "Veg").reduce((s, i) => s + i.qty, 0);
  const nonVegPlates = invoiceData.items.filter(i => i.type === "Non-Veg").reduce((s, i) => s + i.qty, 0);
  const mixedPlates = invoiceData.items.filter(i => i.type === "Mixed").reduce((s, i) => s + i.qty, 0);

  // ✅ PDF Download
  const handleDownloadPDF = async () => {
    const canvas = await html2canvas(invoiceRef.current);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`${invoiceData.invoiceNo}.pdf`);
  };

  // ✅ Print only Invoice Section
  const handlePrint = () => {
    const printContent = invoiceRef.current.innerHTML;
    const win = window.open("", "", "height=700,width=900");
    win.document.write(`
      <html>
        <head>
          <title>Invoice</title>
          <style>
            body { font-family: Arial, sans-serif; padding:20px; }
            table { width:100%; border-collapse: collapse; margin-top:15px; }
            th, td { border:1px solid #ddd; padding:8px; font-size:14px; }
            th { background:#f5f5f5; }
            .summary { margin-top:15px; float:right; width:300px; }
            .summary div { display:flex; justify-content:space-between; margin:4px 0; }
            .grand { font-weight:bold; font-size:16px; border-top:2px solid #000; padding-top:6px; }
            .header { text-align:center; border-bottom:2px solid #000; margin-bottom:15px; padding-bottom:10px; }
            .footer { text-align:center; margin-top:20px; font-size:13px; }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    win.document.close();
    win.print();
  };

  // Dynamic button styles with hover effects
  const buttonStyles = {
    submitBtn: {
      width: "100%",
      padding: "12px",
      border: "none",
      borderRadius: "8px",
      background: "linear-gradient(135deg, #4a6cf7 0%, #3b5bdb 100%)",
      color: "#fff",
      cursor: "pointer",
      fontSize: "16px",
      fontWeight: "600",
      transition: "all 0.3s ease",
      transform: hoverState.submit ? "translateY(-2px)" : "translateY(0)",
      boxShadow: hoverState.submit ? "0 5px 15px rgba(74, 108, 247, 0.4)" : "none"
    },
    printBtn: {
      padding: "12px 20px",
      border: "none",
      borderRadius: "8px",
      background: "linear-gradient(135deg, #27ae60 0%, #219653 100%)",
      color: "#fff",
      cursor: "pointer",
      fontSize: "16px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.3s ease",
      minWidth: "180px",
      justifyContent: "center",
      transform: hoverState.print ? "translateY(-2px)" : "translateY(0)",
      boxShadow: hoverState.print ? "0 5px 15px rgba(39, 174, 96, 0.4)" : "none"
    },
    downloadBtn: {
      padding: "12px 20px",
      border: "none",
      borderRadius: "8px",
      background: "linear-gradient(135deg, #4a6cf7 0%, #3b5bdb 100%)",
      color: "#fff",
      cursor: "pointer",
      fontSize: "16px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.3s ease",
      minWidth: "180px",
      justifyContent: "center",
      transform: hoverState.download ? "translateY(-2px)" : "translateY(0)",
      boxShadow: hoverState.download ? "0 5px 15px rgba(74, 108, 247, 0.4)" : "none"
    }
  };

  return (
    <div style={styles.pageContainer}>
      {/* Invoice Box */}
      <div ref={invoiceRef} style={styles.invoiceContainer}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.logoContainer}>
            <h1 style={styles.restaurantName}>Maamaa's House</h1>
            <p style={styles.tagline}>Food Delivery Services</p>
          </div>
          <div style={styles.invoiceTitle}>
            <h2 style={styles.invoiceText}>INVOICE</h2>
            <div style={styles.invoiceNumber}>{invoiceData.invoiceNo}</div>
          </div>
        </div>

        {/* Invoice Info */}
        <div style={styles.invoiceInfo}>
  {/* Left Side - Invoice Details */}
  <div style={{ flex: 1 }}>
    <h3 style={styles.sectionTitle}>Invoice Details</h3>
    <div style={{ display: "flex", gap: "20px", marginBottom: "8px" }}>
      <div style={styles.infoRow}>
        {/* <span style={styles.infoLabel}>No:</span>
        <span style={styles.infoValue}>{invoiceData.invoiceNo}</span> */}
      </div>
      <div style={styles.infoRow}>
        <span style={styles.infoLabel}>Date:</span>
        <span style={styles.infoValue}>{invoiceData.date}</span>
      </div>
      <div style={styles.infoRow}>
        <span style={styles.infoLabel}>Time:</span>
        <span style={styles.infoValue}>{invoiceData.time}</span>
      </div>
    </div>
  </div>

  {/* Right Side - Delivery Info */}
  <div style={{ flex: 2 }}>
    <h3 style={styles.sectionTitle}>Delivery To</h3>
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>Name:</span>
      <span style={{ ...styles.infoValue, fontWeight: "bold" }}>
        {invoiceData.customer.receiver}
      </span>
    </div>
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>Phone:</span>
      <span style={styles.infoValue}>{invoiceData.customer.phone}</span>
    </div>
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>Address:</span>
      <span style={styles.infoValue}>{invoiceData.customer.location}</span>
    </div>
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>Landmark:</span>
      <span style={styles.infoValue}>{invoiceData.customer.nearby}</span>
    </div>
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>From:</span>
      <span style={styles.infoValue}>{invoiceData.restaurant}</span>
    </div>
    <div style={styles.infoRow}>
        <span style={styles.infoLabel}>Date:</span>
        <span style={styles.infoValue}>{invoiceData.date}</span>
      </div>
      <div style={styles.infoRow}>
        <span style={styles.infoLabel}>Time:</span>
        <span style={styles.infoValue}>{invoiceData.time}</span>
      </div>
    </div>
    </div>
    

        {/* Items Table */}
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Sr.No</th>
                <th style={styles.tableHeader}>Category</th>
                {!isMobile && <th style={styles.tableHeader}>Type</th>}
                <th style={styles.tableHeader}>Item</th>
                <th style={styles.tableHeader}>Qty</th>
                <th style={styles.tableHeader}>Price (₹)</th>
                <th style={styles.tableHeader}>Total (₹)</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.items.map((item, index) => (
                <tr key={item.id} style={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                  <td style={styles.tableCell}>{index + 1}</td>
                  <td style={styles.tableCell}>{item.category}</td>
                  {!isMobile && <td style={styles.tableCell}>{item.type}</td>}
                  <td style={styles.tableCell}>{item.name}</td>
                  <td style={styles.tableCell}>{item.qty}</td>
                  <td style={styles.tableCell}>{item.price}</td>
                  <td style={styles.tableCell}>{item.qty * item.price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Plate Summary */}
        <div style={styles.plateSummary}>
          <h4 style={styles.plateTitle}>🍽️ Plate Summary</h4>
          <div style={styles.plateContainer}>
            <div style={styles.plateItem}>
              <span style={styles.plateLabel}>Veg Plates:</span>
              <span style={styles.plateValue}>{vegPlates}</span>
            </div>
            <div style={styles.plateItem}>
              <span style={styles.plateLabel}>Non-Veg Plates:</span>
              <span style={styles.plateValue}>{nonVegPlates}</span>
            </div>
            <div style={styles.plateItem}>
              <span style={styles.plateLabel}>Mixed Plates:</span>
              <span style={styles.plateValue}>{mixedPlates}</span>
            </div>
          </div>
        </div>

        {/* Payment Summary */}
        <div style={styles.summary}>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Subtotal:</span>
            <span style={styles.summaryValue}>₹{subTotal.toFixed(2)}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Delivery Charge:</span>
            <span style={styles.summaryValue}>₹{invoiceData.deliveryCharge}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Platform Charge:</span>
            <span style={styles.summaryValue}>₹{invoiceData.platformCharge}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Discount ({invoiceData.discountPercent}%):</span>
            <span style={{...styles.summaryValue, color: '#e74c3c'}}>-₹{discount.toFixed(2)}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>SGST ({invoiceData.sgstPercent}%):</span>
            <span style={styles.summaryValue}>₹{sgst.toFixed(2)}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>CGST ({invoiceData.cgstPercent}%):</span>
            <span style={styles.summaryValue}>₹{cgst.toFixed(2)}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Advance Paid:</span>
            <span style={{...styles.summaryValue, color: '#e74c3c'}}>-₹{invoiceData.advancePaid}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Dues Remaining:</span>
            <span style={{...styles.summaryValue, fontWeight: 'bold'}}>₹{dues.toFixed(2)}</span>
          </div>
          <div style={{...styles.summaryRow, ...styles.grandTotal}}>
            <span style={styles.grandLabel}>Grand Total:</span>
            <span style={styles.grandValue}>₹{grandTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <div style={styles.paymentStatus}>
            <strong>Payment Status:</strong> {dues > 0 ? "Partial (Advance Paid)" : "Paid in Full"}
          </div>
          <p style={styles.thankYou}>🙏 Thank you for your order!</p>
          <div style={styles.footerNote}>
            <p>For queries, contact: +91 9876543210</p>
            <p>Email: support@maamaahouse.com</p>
          </div>
        </div>
      </div>

      {/* Feedback Section */}
      <div style={styles.feedback}>
        <h3 style={styles.feedbackTitle}>Give Feedback</h3>
        <textarea
          style={styles.textarea}
          placeholder="Write your feedback..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
        />
        <div style={styles.rating}>
          <p style={styles.ratingText}>Rate your experience:</p>
          <div style={styles.stars}>
            {[1, 2, 3, 4, 5].map(star => (
              <span
                key={star}
                style={{ 
                  cursor: "pointer", 
                  fontSize: "28px", 
                  margin: "0 4px",
                  color: rating >= star ? "#FFD700" : "#ccc",
                  transition: "color 0.2s ease"
                }}
                onClick={() => setRating(star)}
                onMouseOver={(e) => {
                  if (!rating) e.target.style.color = "#FFD700";
                }}
                onMouseOut={(e) => {
                  if (!rating) e.target.style.color = "#ccc";
                }}
              >
                ★
              </span>
            ))}
          </div>
        </div>
        <button 
          style={buttonStyles.submitBtn}
          onMouseEnter={() => handleHover('submit', true)}
          onMouseLeave={() => handleHover('submit', false)}
        >
          Submit Feedback
        </button>
      </div>

      {/* Action Buttons */}
      <div style={styles.actions}>
        <button 
          style={buttonStyles.printBtn} 
          onClick={handlePrint}
          onMouseEnter={() => handleHover('print', true)}
          onMouseLeave={() => handleHover('print', false)}
        >
          <span style={styles.btnIcon}>🖨</span> Print Invoice
        </button>
        <button 
          style={buttonStyles.downloadBtn} 
          onClick={handleDownloadPDF}
          onMouseEnter={() => handleHover('download', true)}
          onMouseLeave={() => handleHover('download', false)}
        >
          <span style={styles.btnIcon}>⬇</span> Download PDF
        </button>
      </div>
    </div>
  );
};

// Styles
const styles = {
  pageContainer: {
    padding: "15px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
    // background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    minHeight: "100vh",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  invoiceContainer: {
    width: "100%",
    maxWidth: "850px",
    padding: "25px",
    border: "1px solid #ddd",
    borderRadius: "12px",
    background: "#fff",
    color: "#333",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "25px",
    paddingBottom: "20px",
    borderBottom: "2px solid #bb5fd4ff",
    flexWrap: "wrap",
  },
  logoContainer: {
    textAlign: "left",
  },
  restaurantName: {
    color: "#bb5fd4ff",
    margin: "0",
    fontSize: "28px",
    fontWeight: "700",
  },
  tagline: {
    color: "#666",
    margin: "5px 0 0 0",
    fontSize: "14px",
  },
  invoiceTitle: {
    textAlign: "right",
  },
  invoiceText: {
    color: "#bb5fd4ff",
    margin: "0",
    fontSize: "24px",
    fontWeight: "700",
  },
  invoiceNumber: {
    backgroundColor: "#bb5fd4ff",
    color: "white",
    padding: "5px 10px",
    borderRadius: "4px",
    fontSize: "14px",
    marginTop: "5px",
    display: "inline-block",
  },
  invoiceInfo: {
    display: "flex",
    justifyContent: "space-between",
    gap: "40px",
    marginBottom: "20px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    marginBottom: "6px",
    borderBottom: "1px solid #ddd",
    paddingBottom: "4px",
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: "4px",
    fontSize: "14px",
  },
  infoLabel: {
    fontWeight: "600",
    marginRight: "6px",
  },
  infoValue: {
    color: "#333",
  },
  sectionTitle: {
    color: "#bb5fd4ff",
    marginBottom: "15px",
    fontSize: "18px",
    borderBottom: "1px solid #eee",
    paddingBottom: "8px",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
    padding: "5px 0",
  },
  infoLabel: {
    fontWeight: "600",
    color: "#555",
    minWidth: "80px",
  },
  infoValue: {
    textAlign: "right",
    color: "#333",
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
    marginBottom: "20px",
    borderRadius: "8px",
    border: "1px solid #eee",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
    minWidth: "600px",
  },
  tableHeader: {
    backgroundColor: "#bb5fd4ff",
    color: "white",
    padding: "12px 10px",
    textAlign: "left",
    fontWeight: "600",
  },
  tableCell: {
    padding: "12px 10px",
    borderBottom: "1px solid #eee",
  },
  evenRow: {
    backgroundColor: "#f9f9f9",
  },
  oddRow: {
    backgroundColor: "white",
  },
  plateSummary: {
    marginBottom: "20px",
    padding: "15px",
    borderRadius: "8px",
    backgroundColor: "#f8f9fa",
    borderLeft: "4px solid #bb5fd4ff",
  },
  plateTitle: {
    margin: "0 0 10px 0",
    color: "#bb5fd4ff",
    fontSize: "16px",
  },
  plateContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "15px",
  },
  plateItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  plateLabel: {
    fontWeight: "600",
    color: "#555",
  },
  plateValue: {
    fontWeight: "bold",
    color: "#bb5fd4ff",
  },
  summary: {
    borderTop: "2px solid #bb5fd4ff",
    paddingTop: "15px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    maxWidth: "400px",
    marginLeft: "auto",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  summaryLabel: {
    color: "#555",
  },
  summaryValue: {
    fontWeight: "500",
  },
  grandTotal: {
    borderTop: "2px solid #bb5fd4ff",
    paddingTop: "10px",
    marginTop: "5px",
  },
  grandLabel: {
    fontWeight: "bold",
    fontSize: "18px",
    color: "#bb5fd4ff",
  },
  grandValue: {
    fontWeight: "bold",
    fontSize: "18px",
    color: "#bb5fd4ff",
  },
  footer: {
    marginTop: "30px",
    textAlign: "center",
    padding: "15px",
    borderRadius: "8px",
    backgroundColor: "#f8f9fa",
  },
  paymentStatus: {
    fontSize: "16px",
    marginBottom: "10px",
    // color: dues > 0 ? "#e67e22" : "#27ae60",
    fontWeight: "600",
  },
  thankYou: {
    margin: "10px 0",
    fontSize: "16px",
    color: "#bb5fd4ff",
  },
  footerNote: {
    fontSize: "13px",
    color: "#777",
    marginTop: "10px",
  },
  feedback: {
    width: "100%",
    maxWidth: "850px",
    padding: "25px",
    border: "1px solid #ddd",
    borderRadius: "10px",
    background: "#fff",
    boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
  },
  feedbackTitle: {
    color: "#bb5fd4ff",
    marginBottom: "15px",
    textAlign: "center",
  },
  textarea: {
    width: "100%",
    minHeight: "100px",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    marginBottom: "15px",
    fontSize: "14px",
    resize: "vertical",
    fontFamily: "inherit",
  },
  rating: {
    marginBottom: "20px",
    textAlign: "center",
  },
  ratingText: {
    marginBottom: "10px",
    color: "#555",
  },
  stars: {
    display: "flex",
    justifyContent: "center",
  },
  btnIcon: {
    fontSize: "18px",
  },
  actions: {
    display: "flex",
    gap: "15px",
    flexWrap: "wrap",
    justifyContent: "center",
    width: "100%",
    maxWidth: "850px",
  },
};

export default InvoicePage;